import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Fade,
} from "@mui/material";
import {
    Email as EmailIcon,
    CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { database } from "../../firebase";
import { ref, push, set } from "firebase/database";

const EmailSubscription = () => {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email.trim()) {
            setMessage("Please enter your email address.");
            setMessageType("error");
            return;
        }

        if (!validateEmail(email)) {
            setMessage("Please enter a valid email address.");
            setMessageType("error");
            return;
        }

        setIsLoading(true);
        setMessage("");

        try {
            const subscribersRef = ref(database, "subscribers");
            const newSubscriberRef = push(subscribersRef);

            await set(newSubscriberRef, {
                email: email.trim(),
                timestamp: new Date().toISOString(),
                status: "active",
            });

            setMessage(
                "🎉 Thank you for subscribing! We'll keep you updated on new features."
            );
            setMessageType("success");
            setEmail("");
        } catch (error) {
            console.error("Error saving email:", error);
            setMessage("Sorry, there was an error. Please try again later.");
            setMessageType("error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                py: { xs: 8, md: 12, lg: 16 },
                background: "linear-gradient(135deg, #2E8B57 0%, #228B22 100%)",
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
            radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
          `,
                    pointerEvents: "none",
                },
            }}
        >
            <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
                <Fade in timeout={1000}>
                    <Card
                        sx={{
                            background: "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(20px)",
                            borderRadius: 4,
                            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                    >
                        <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                            <Box sx={{ textAlign: "center", mb: 4 }}>
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    sx={{
                                        mb: 2,
                                        fontWeight: 700,
                                        color: "text.primary",
                                    }}
                                >
                                    Stay Updated with Quran Chat Buddy
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: "text.secondary",
                                        lineHeight: 1.7,
                                        maxWidth: 500,
                                        mx: "auto",
                                    }}
                                >
                                    Be the first to know about new features,
                                    updates, and Islamic content. Join our
                                    mailing list for exclusive early access.
                                </Typography>
                            </Box>

                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: 2,
                                    mb: 3,
                                }}
                            >
                                <TextField
                                    fullWidth
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email address"
                                    variant="outlined"
                                    disabled={isLoading}
                                    InputProps={{
                                        startAdornment: (
                                            <EmailIcon
                                                sx={{
                                                    color: "primary.main",
                                                    mr: 1,
                                                }}
                                            />
                                        ),
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            py: 0.5,
                                            fontSize: "1rem",
                                        },
                                    }}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    disabled={isLoading}
                                    startIcon={
                                        isLoading ? (
                                            <CircularProgress
                                                size={20}
                                                color="inherit"
                                            />
                                        ) : (
                                            <CheckCircleIcon />
                                        )
                                    }
                                    sx={{
                                        py: 1.5,
                                        px: 4,
                                        fontSize: "1rem",
                                        fontWeight: 600,
                                        minWidth: { xs: "100%", sm: 160 },
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {isLoading ? "Subscribing..." : "Subscribe"}
                                </Button>
                            </Box>

                            {message && (
                                <Fade in>
                                    <Alert
                                        severity={messageType}
                                        sx={{
                                            mb: 3,
                                            borderRadius: 2,
                                            fontWeight: 500,
                                        }}
                                    >
                                        {message}
                                    </Alert>
                                </Fade>
                            )}

                            <Typography
                                variant="body2"
                                sx={{
                                    textAlign: "center",
                                    color: "text.secondary",
                                    lineHeight: 1.6,
                                }}
                            >
                                We respect your privacy. Unsubscribe at any
                                time.{" "}
                                <Box
                                    component="a"
                                    href="/privacy-policy"
                                    sx={{
                                        color: "primary.main",
                                        textDecoration: "underline",
                                        "&:hover": {
                                            color: "primary.dark",
                                        },
                                    }}
                                >
                                    Read our Privacy Policy
                                </Box>
                                .
                            </Typography>
                        </CardContent>
                    </Card>
                </Fade>
            </Container>
        </Box>
    );
};

export default EmailSubscription;
